import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isApiError, tournamentApi } from "@bw/shared/api";
import type {
  TournamentPlayerEntryResponse,
  SoloEntryCreate,
} from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * The player ENTER-SOLO mutation (SLICE 1.4-a) — POST
 * /tournaments/draws/{draw_id}/entries/solo via tournamentApi.enterSolo, body the
 * SoloEntryCreate VERBATIM ({ accept_legal_terms, acceptances?, payment_method_id? };
 * includes the server-issued expected_quote but never computes a fee). Mirrors
 * useRegisterEntry but for the doubles/mixed SOLO
 * flow ("I'll find a partner later") — the created entry is `partner_wanted`.
 * MONEY-touching (the paid path charges the chosen card server-side): the hook
 * owns NONE of the money logic — it forwards the body and lets the backend charge.
 *
 * On SUCCESS it invalidates the tournament-scoped registrations + eligibility
 * queries (registrationsMine + eligibility) so the per-event list
 * (TournamentEventList) refetches and the row flips to the "Needs a partner"
 * state — the affirmation comes from the SERVER's refetched entry, never an
 * optimistic local flip. A FAILED solo entry invalidates nothing (the row stays
 * "Enter solo"), so a failed charge can never fabricate an entered state.
 *
 * `tournamentId` is needed ONLY to key the invalidation (the solo endpoint itself
 * is draw-scoped). The dialog owns the pending/disabled gating off
 * `isPending`/`isSuccess` for the one-charge double-submit guard.
 */
export function useEnterSolo(tournamentId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<
    TournamentPlayerEntryResponse,
    unknown,
    { drawId: string; body: SoloEntryCreate }
  >({
    mutationFn: ({ drawId, body }) => tournamentApi.enterSolo(drawId, body),
    onSettled: () => queryClient.invalidateQueries({
      queryKey: queryKeys.tournaments.myRegistrationPayments(),
    }),
    onSuccess: async () => {
      const id = tournamentId ?? "";
      void queryClient.invalidateQueries({
        queryKey: queryKeys.tournaments.registrationsMine(id),
      });
      // Entering an event can change the remaining verdicts (e.g. an EBC
      // max-events / duplicate-discipline cap now applies), so refetch the
      // verdicts too — the list greys newly-blocked rows from server truth.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.tournaments.eligibility(id),
      });
      // The per-event aggregate entry_count lives on the events-list query, so a
      // successful entry/withdrawal must refetch it too — otherwise the row's
      // "N entries" count goes stale until a hard refresh.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.tournaments.events(id),
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.tournaments.myRegistrations(),
      });
    },
    onError: (error) => {
      if (isApiError(error) && error.code === "REGISTRATION_PRICE_CHANGED") {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.tournaments.eligibility(tournamentId ?? ""),
        });
      }
    },
  });
}
